import { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { prepareEditorBrowserSession } from "../lib/editorBrowserSession";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios global config: send cookies on every request (auth lives in httpOnly cookie)
axios.defaults.withCredentials = true;

// Resiliencia del gate de auth. El bug "Loading… un minuto" NO era el backend
// (auth/me responde en ~5ms; medido). Era una petición /api/auth/me que quedaba
// PENDIENTE en el navegador — socket ocupado / red intermitente / SW colgado —
// y como el gate (ProtectedRoute) bloquea toda la app mientras `loading===true`,
// una sola petición atascada dejaba la pantalla negra indefinidamente.
//
// Fix de raíz: cada intento se corta con AbortController (libera el socket
// muerto) y se REINTENTA con backoff, en vez de rendirse. Clave anti-regresión:
// un timeout ciego que hiciera setUser(null) DESLOGUEARÍA en falso a un usuario
// válido cuya red va lenta. Por eso distinguimos:
//   - 401 explícito del backend  → no autenticado de verdad → login, sin retry.
//   - timeout / error de red      → sesión desconocida → REINTENTAR, no deslogar.
// Solo tras agotar los reintentos de red caemos a login (recuperable recargando),
// que sigue siendo mejor que un negro infinito sin escape.
const AUTHME_TIMEOUT_MS = 12000;   // corta el socket colgado por intento
const AUTHME_MAX_RETRIES = 4;      // reintentos ante fallo de red (no 401)
const AUTHME_RETRY_BASE_MS = 800;  // backoff: 0.8s, 1.6s, 3.2s...

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const AuthContext = createContext({
  user: null, loading: true, refresh: () => {}, logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    for (let attempt = 0; attempt < AUTHME_MAX_RETRIES; attempt += 1) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), AUTHME_TIMEOUT_MS);
      try {
        const r = await axios.get(`${API}/auth/me`, { signal: controller.signal });
        clearTimeout(timer);
        prepareEditorBrowserSession(r.data?.user_id);
        setUser(r.data);
        setLoading(false);
        return;
      } catch (e) {
        clearTimeout(timer);
        // 401/403 = respuesta AUTORITATIVA del backend: no hay sesión. No se
        // reintenta ni se insiste — se manda a login (setUser(null)).
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          setUser(null);
          setLoading(false);
          return;
        }
        // Cualquier otra cosa (abort por timeout, red caída, 5xx transitorio):
        // NO deslogamos. Reintentamos con backoff — el socket nuevo suele
        // resolver lo que el atascado no podía.
        // eslint-disable-next-line no-console
        console.warn(
          `[auth] /me intento ${attempt + 1}/${AUTHME_MAX_RETRIES} falló:`,
          e?.code || e?.message || e,
        );
        if (attempt < AUTHME_MAX_RETRIES - 1) {
          // eslint-disable-next-line no-await-in-loop
          await sleep(AUTHME_RETRY_BASE_MS * 2 ** attempt);
        }
      }
    }
    // Agotados los reintentos de red: caemos a login (recuperable recargando).
    // Es un estado terminal explícito, no una pantalla negra infinita.
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); }
    catch (e) { console.warn("[auth] logout request failed:", e?.message || e); }
    prepareEditorBrowserSession(null);
    setUser(null);
    window.location.href = "/login";
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
